import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, unwrap } from '@/shared/api-client/workspace';

export type Weekday =
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY'
  | 'SUNDAY';

export interface VenueWeekdayHours {
  weekday: Weekday;
  openTime: string | null;
  closeTime: string | null;
}

export interface VenueOperatingHours {
  venueId: string;
  configured?: boolean;
  weekdayHours: VenueWeekdayHours[];
}

export interface UpdateVenueOperatingHoursInput {
  weekdayHours: VenueWeekdayHours[];
}

const settingsKey = (venueId: string) => ['venues', venueId, 'operating-hours'] as const;
const operatingHoursPath = '/api/v1/venue-workspace/venues/{venueId}/operating-hours';

const getVenueOperatingHours = (venueId: string): Promise<VenueOperatingHours> =>
  unwrap(
    apiClient.GET(operatingHoursPath, { params: { path: { venueId } } }),
  ) as Promise<VenueOperatingHours>;

const updateVenueOperatingHours = (
  venueId: string,
  input: UpdateVenueOperatingHoursInput,
): Promise<VenueOperatingHours> =>
  unwrap(
    apiClient.PATCH(operatingHoursPath, { params: { path: { venueId } }, body: input }),
  ) as Promise<VenueOperatingHours>;

export function useVenueOperatingHours(venueId: string | null) {
  return useQuery({
    queryKey: settingsKey(venueId ?? ''),
    queryFn: () => getVenueOperatingHours(venueId!),
    enabled: venueId !== null,
  });
}

export function useUpdateVenueOperatingHours(venueId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateVenueOperatingHoursInput) =>
      updateVenueOperatingHours(venueId, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: settingsKey(venueId) }),
  });
}
